import { useId, useState } from "react";

import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import {
  EMAIL_OFF_SENTENCE,
  SEND_OFFLINE_SENTENCE,
  testSentSentence,
} from "../sendCopy";
import { BODY_TEXT, STEP_HEADING } from "../ui";
import { useSendFlow } from "../useSendFlow";

type Kind = "invite" | "tonight";

const KINDS: { id: Kind; title: string; blurb: string; audience: string }[] = [
  {
    id: "invite",
    title: "Invitation",
    blurb: "The original invitation to the 18 and 20 September evenings.",
    audience: "every organisation on the list",
  },
  {
    id: "tonight",
    title: "Tonight, last chance",
    blurb:
      "On the day of the leaders session: it is tonight and not too late. Skips organisations that have already registered.",
    audience: "every organisation on the list that hasn't registered",
  },
];

/**
 * The September 2026 community leaders invitation: a one-off send to a
 * list of community organisations kept on the server, not to people who
 * booked. One email per organisation. Organisers only.
 *
 * The page's test is the backend's "test" mode. The backend's separate
 * "dry-run" mode and its "reminder" kind aren't used from here.
 */
export function CommunityInvitePanel({
  defaultTestEmail,
}: {
  defaultTestEmail: string;
}) {
  const flow = useSendFlow(defaultTestEmail);
  const utils = trpc.useUtils();
  const invite = trpc.adminSendCommunityInvite.useMutation();
  const [kind, setKind] = useState<Kind>("invite");
  const groupLabelId = useId();

  const send = (mode: "test" | "all", testEmails: string[]) => {
    invite.mutate(
      { mode, kind, testEmails: mode === "test" ? testEmails : undefined },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped ? EMAIL_OFF_SENTENCE : testSentSentence(res.sentTo),
            );
          } else if (res.mode === "all") {
            const sentences = [
              res.sent === 0 && res.failed === 0 && res.skipped > 0
                ? EMAIL_OFF_SENTENCE
                : `Sent to ${res.sent} of ${res.orgs} organisations.`,
            ];
            if (res.failed > 0) {
              sentences.push(`${res.failed} didn't go through.`);
            }
            flow.sendAllDone(sentences.join(" "));
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  const chosen = KINDS.find((k) => k.id === kind) ?? KINDS[0];

  return (
    <>
      <p className={BODY_TEXT}>
        Shekher's letters to the community organisations on our list. This is a
        separate list we put together, not people who have booked. Each
        organisation gets one email. Send yourself a test first.
      </p>
      <p id={groupLabelId} className={`mt-5 ${STEP_HEADING}`}>
        Which letter?
      </p>
      <div
        role="radiogroup"
        aria-labelledby={groupLabelId}
        className="mt-2 grid gap-2.5 md:grid-cols-2"
      >
        {KINDS.map((k) => {
          const active = kind === k.id;
          return (
            <button
              key={k.id}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={invite.isPending}
              onClick={() => {
                if (!active) {
                  setKind(k.id);
                  flow.optionsChanged();
                }
              }}
              className={`min-h-11 rounded-lg border p-3.5 text-left transition-colors ${
                active
                  ? "border-2 border-[color:var(--color-amber)] bg-[color:var(--color-paper)]"
                  : "border-[color:var(--color-line)] hover:border-[color:var(--color-line-strong)]"
              }`}
            >
              <span className="block text-[15px] font-semibold text-[color:var(--color-ink)]">
                {k.title}
              </span>
              <span className="mt-1 block text-[14px] text-[color:var(--color-ink-soft)]">
                {k.blurb}
              </span>
            </button>
          );
        })}
      </div>
      <TestThenSend
        flow={flow}
        emailName={chosen.title}
        audience={chosen.audience}
        noAudienceText=""
        multipleAddresses
        busy={invite.isPending}
        onSendTest={(addresses) => send("test", addresses)}
        onSendAll={() => send("all", [])}
      />
    </>
  );
}
